import { useState, useEffect } from "react";
// v1.27.0: Badge import removed — ProfilePage's only Badge usage was the
// party badge below the name/role/location stack, which has been deleted.
import { Ava } from "../atoms/Ava.jsx";
import { Logo } from "../atoms/Logo.jsx";
import { CopySlug } from "../atoms/CopySlug.jsx";
import { appUrl } from "../utils/appUrl.js";
import { apiFetch } from "../services/api.js";
import { personaCtaName } from "../utils/personaCta.js";

export function ProfilePage({persona, onStartChat, onBack}) {
  const pc = persona.accentColor;
  const localMsgs = persona.conversations.reduce((a,c) => a + c.messages.filter(m => m.role === "user").length, 0);
  const localLikes = persona.feedback.filter(f => f.type === "like").length;
  const bioParagraphs = (persona.bio || persona.statement).split("\n\n").filter(Boolean);

  // Fetch engagement stats from API
  const [engagement, setEngagement] = useState(null);
  useEffect(() => {
    apiFetch(`/api/candidates/${persona.id}/engagement`)
      .then(d => setEngagement(d))
      .catch(() => {});
  }, [persona.id]);

  return (
    <div style={{minHeight:"100vh",background:"#090e1a",paddingBottom:40}}>
      <div style={{background:`linear-gradient(160deg,${pc}28 0%,${pc}08 60%,#090e1a 100%)`,paddingBottom:28}}>
        <div style={{padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <button onClick={onBack} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",display:"flex",alignItems:"center",gap:7,padding:0,flexShrink:0}}>
            <span style={{fontSize:18,lineHeight:1}}>←</span>
            <Logo size={15}/>
          </button>
          <CopySlug slug={persona.slug} color={pc}/>
        </div>

        <div style={{padding:"16px 18px 0",display:"flex",alignItems:"center",gap:18}}>
          <div style={{position:"relative",flexShrink:0}}>
            <Ava ini={persona.avatar} color={pc} size={88} photoUrl={persona.photoUrl}/>
            <div style={{position:"absolute",bottom:3,right:3,width:16,height:16,borderRadius:"50%",background:"#22c55e",border:"2px solid #090e1a"}}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <h1 style={{margin:"0 0 4px",color:"#f1f5f9",fontSize:22,fontWeight:900,fontFamily:"Georgia,serif",lineHeight:1.15}}>{persona.name}</h1>
            <div style={{color:"#94a3b8",fontSize:14,fontWeight:500,marginBottom:4}}>{persona.role}</div>
            {persona.location && <div style={{color:"#64748b",fontSize:12,marginBottom:7}}>📍 {persona.location}</div>}
          </div>
        </div>

        <div style={{margin:"16px 18px 0",borderLeft:`3px solid ${pc}`,paddingLeft:12}}>
          <p style={{margin:0,color:"#94a3b8",fontSize:14,lineHeight:1.65,fontStyle:"italic"}}>"{persona.tagline}"</p>
        </div>

        {/* Stats bar */}
        <div style={{margin:"20px 18px 0",display:"flex",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:12,overflow:"hidden"}}>
          {[[(engagement?.totalSessions ?? persona.conversations.length),"Sessions","#f1f5f9"],[(engagement?.totalMessages ?? localMsgs),"Questions","#f1f5f9"],[(engagement?.helpfulCount ?? localLikes),"Helpful","#22c55e"]].map(([val,label,color],i) => (
            <div key={label} style={{flex:1,padding:"14px 8px",textAlign:"center",borderRight:i<2?"1px solid #1e2d45":"none"}}>
              <div style={{fontSize:20,fontWeight:900,color,fontFamily:"Georgia,serif"}}>{val}</div>
              <div style={{fontSize:11,color:"#64748b",marginTop:2}}>{label}</div>
            </div>
          ))}
        </div>

        {/* Bug 5: Citizen Engagement summary (shown when API data is available) */}
        {engagement && (engagement.uniqueVoters > 0 || engagement.totalMessages > 0) && (
          <div style={{margin:"12px 18px 0",background:"#0d1724",border:`1px solid ${pc}22`,borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:10}}>Citizen Engagement</div>
            <div style={{display:"flex",gap:0,marginBottom: engagement.topQuestions?.length ? 12 : 0,borderBottom: engagement.topQuestions?.length ? "1px solid #1e2d45" : "none",paddingBottom: engagement.topQuestions?.length ? 12 : 0}}>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{fontSize:18,fontWeight:900,color:"#f1f5f9",fontFamily:"Georgia,serif"}}>{engagement.uniqueVoters.toLocaleString()}</div>
                <div style={{fontSize:10,color:"#64748b",marginTop:2}}>Citizens Engaged</div>
              </div>
              <div style={{width:1,background:"#1e2d45"}}/>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{fontSize:18,fontWeight:900,color:"#f1f5f9",fontFamily:"Georgia,serif"}}>{engagement.totalMessages.toLocaleString()}</div>
                <div style={{fontSize:10,color:"#64748b",marginTop:2}}>Questions Asked</div>
              </div>
            </div>
            {engagement.topQuestions?.length > 0 && (
              <div>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",color:"#475569",textTransform:"uppercase",marginBottom:8}}>Citizens are asking…</div>
                {engagement.topQuestions.slice(0,3).map((q,i) => (
                  <div key={i} style={{fontSize:12,color:"#64748b",padding:"6px 0",borderTop:i?"1px solid #0f1e30":undefined,lineHeight:1.5}}>
                    <span style={{color:pc,fontWeight:700,fontSize:10,marginRight:5}}>Q</span>"{q}"
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{padding:"18px 18px 0"}}>
          {persona.outOfGeo ? (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{width:"100%",padding:"14px",background:"#1e2d45",color:"#64748b",border:"1px solid #2d3f5a",borderRadius:12,fontSize:14,fontWeight:600,fontFamily:"inherit",textAlign:"center"}}>
                🌍 You are not within this candidate's jurisdiction
              </div>
              <button onClick={onBack} style={{background:"none",border:"none",color:"#60a5fa",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",padding:0}}>
                ← Browse candidates in your area
              </button>
            </div>
          ) : (
            <button onClick={onStartChat} style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${pc},${pc}cc)`,color:"#fff",border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              💬 Ask {personaCtaName(persona)} a Question
            </button>
          )}
        </div>
      </div>

      <div className="qm-profile-body" style={{maxWidth:800,margin:"0 auto",padding:"20px 18px"}}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:14,padding:20}}>
            <h2 style={{margin:"0 0 14px",color:"#f1f5f9",fontSize:15,fontWeight:800,fontFamily:"Georgia,serif"}}>About</h2>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {bioParagraphs.map((p,i) => <p key={i} style={{color:"#94a3b8",fontSize:14,lineHeight:1.8,margin:0}}>{p}</p>)}
            </div>
          </div>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:14,padding:20}}>
            <h2 style={{margin:"0 0 14px",color:"#f1f5f9",fontSize:15,fontWeight:800,fontFamily:"Georgia,serif"}}>Popular Questions</h2>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {persona.suggestedPrompts?.map((q,i) => (
                <button key={i} onClick={persona.outOfGeo ? undefined : onStartChat} disabled={!!persona.outOfGeo} style={{padding:"11px 14px",background:"#090e1a",border:`1px solid ${pc}25`,borderRadius:9,color:"#cbd5e1",fontSize:13,cursor:persona.outOfGeo?"default":"pointer",textAlign:"left",fontFamily:"inherit",lineHeight:1.45,display:"flex",alignItems:"flex-start",gap:10,opacity:persona.outOfGeo?0.5:1}}>
                  <span style={{color:pc,fontWeight:800,fontSize:12,flexShrink:0,marginTop:1}}>Q</span>
                  <span>{q}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:14,padding:18}}>
            <h3 style={{margin:"0 0 12px",color:"#f1f5f9",fontSize:14,fontWeight:800,fontFamily:"Georgia,serif"}}>Key Positions</h3>
            <div style={{display:"flex",flexDirection:"column",gap:9}}>
              {persona.keyPolicies?.map(p => (
                <div key={p} style={{display:"flex",alignItems:"flex-start",gap:9}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:pc,flexShrink:0,marginTop:5}}/>
                  <span style={{color:"#94a3b8",fontSize:13,lineHeight:1.4}}>{p}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{background:"#0d1724",border:`1px solid ${pc}30`,borderRadius:14,padding:18}}>
            <h3 style={{margin:"0 0 12px",color:"#f1f5f9",fontSize:14,fontWeight:800,fontFamily:"Georgia,serif"}}>Share Profile</h3>
            <div style={{background:"#090e1a",border:"1px solid #1e2d45",borderRadius:8,padding:"9px 11px",fontFamily:"monospace",fontSize:12,color:"#64748b",marginBottom:10,wordBreak:"break-all",lineHeight:1.5}}>
              {appUrl(persona.slug)}
            </div>
            <CopySlug slug={persona.slug} color={pc}/>
          </div>
          {/* LEGAL-5: Independence notice + LEGAL-4: AI disclaimer */}
          <div style={{background:"#0d172488",border:"1px solid #1e2d4560",borderRadius:14,padding:"14px 16px"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
              <span style={{fontSize:14,flexShrink:0}}>⚖️</span>
              <p style={{margin:0,color:"#64748b",fontSize:12,lineHeight:1.6}}>
                <strong style={{color:"#94a3b8"}}>Independent Platform</strong> — QuestionMe is not affiliated with, endorsed by, or operated on behalf of any political party, government body, or candidate. All candidate profiles are created from publicly available information.
              </p>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              <span style={{fontSize:14,flexShrink:0}}>🤖</span>
              <p style={{margin:0,color:"#64748b",fontSize:12,lineHeight:1.6}}>
                <strong style={{color:"#94a3b8"}}>AI-Generated Responses</strong> — Chat responses are generated by AI trained on publicly available statements and positions. They may not accurately reflect the candidate's current views.{" "}
                <a href={`${(import.meta.env.VITE_LANDING_URL ?? "").replace(/\/$/, "")}/disclaimer`} target="_blank" rel="noopener noreferrer" style={{color:"#3b82f6",textDecoration:"underline"}}>Full disclaimer</a>
              </p>
            </div>
          </div>

          {persona.outOfGeo ? (
            <div style={{display:"flex",flexDirection:"column",gap:10,alignItems:"center"}}>
              <div style={{width:"100%",padding:"13px",background:"#1e2d45",color:"#64748b",border:"1px solid #2d3f5a",borderRadius:12,fontSize:13,fontWeight:600,fontFamily:"inherit",textAlign:"center"}}>
                🌍 Chat is only available to voters within this candidate's jurisdiction
              </div>
              <button onClick={onBack} style={{background:"none",border:"none",color:"#60a5fa",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",padding:0}}>
                ← Browse candidates in your area
              </button>
            </div>
          ) : (
            <button onClick={onStartChat} style={{width:"100%",padding:"13px",background:`linear-gradient(135deg,${pc},${pc}cc)`,color:"#fff",border:"none",borderRadius:12,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              💬 Start Chatting
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
